/**********************
 * Content events Virtual Rooms
 *********************/

import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import {
  Button,
  Col,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
} from "reactstrap"
import { Table as AntdTable, Switch, Tooltip, Input as AntdInput, Form as AntdForm } from "antd"
import {
  acDeleteVirtualRoom,
  acCreateUpdateVirtualRoom,
} from "helpers/backend_helper"
import { getLottieLoadingOptions, showToast } from "utils/general"
import EventImagePicker from "../Components/EventImagePicker"
import * as Yup from "yup"
import { useFormik } from "formik"
import Lottie from "react-lottie"

const VirtualRoomsContent = props => {
  const abortRef = useRef(null)
  useEffect(() => {
    abortRef.current = new AbortController()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (!props.virtualRoomsModal) virtualValidation.resetForm()
  }, [props.virtualRoomsModal])

  // Delete room
  const deleteRoom = () => {
    if (!props.ongoingAction && props.selectedRoomId != null) {
      props.setOngoingAction(true)
      let id = Number.parseInt(props.selectedRoomId, 10)
      acDeleteVirtualRoom(abortRef.current, id, props.isProduction)
        .then(result => {
          //console.log("Delete result: ", result)
          if (result.statusCode == 200) {
            showToast(props.t("Deleted"), "success")
            props.getVirtualRooms(props.selectedTreeOption)
            props.getConversations(props.selectedTreeOption)
          } else {
            showToast(props.t("Error"), "error")
          }
        })
        .catch(err => {
          console.log(err)
          showToast(props.t("Error"), "error")
        })
        .finally(() => {
          props.setOngoingAction(false)
          props.setDeleteRoomModal(false)
          props.setSelectedRoomId(null)
        })
    }
  }

  // Virtual room validation
  const virtualValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: props.selectedRoom?.id || undefined,
      type: props.selectedRoom?.type || 1,
      name: props.selectedRoom?.name || "",
      requiresHost: props.selectedRoom?.requiresHost || false,
      opensAt: props.selectedRoom?.opensAt || "",
      closesAt: props.selectedRoom?.closesAt || "",
      imageBase64: props.selectedRoom?.imageBase64 || "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required(props.t("Required field"))
        .trim()
        .min(5, props.t("Too short"))
        .max(30, props.t("Too long")),
      opensAt: Yup.string()
        .matches(/^([0-1]\d|2[0-3]):[0-5]\d:[0-5]\d$/, "HH:mm:ss")
        .required(props.t("Required field")),
      closesAt: Yup.string()
        .matches(/^([0-1]\d|2[0-3]):[0-5]\d:[0-5]\d$/, "HH:mm:ss")
        .required(props.t("Required field")),
    }),
    onSubmit: values => {
      //console.log("Submit room: ", values)
      let imageChanged = values.imageBase64 != props.selectedRoom?.imageBase64
      if (!props.ongoingAction && props.selectedTreeOption) {
        props.setOngoingAction(true)
        acCreateUpdateVirtualRoom(
          abortRef.current,
          props.selectedTreeOption,
          values.type,
          values.name,
          values.requiresHost,
          values.opensAt,
          values.closesAt,
          imageChanged ? values.imageBase64 : undefined,
          values.id,
          props.isProduction
        )
          .then(result => {
            //console.log("Add or update room result: ", result)
            if (result.statusCode === 200 && result.vmr_id) {
              if (result.vmr_type == 1) {
                props.getVirtualRooms(props.selectedTreeOption)
              } else {
                props.getConversations(props.selectedTreeOption)
              }
              props.setVirtualRoomsModal(false)
            } else {
              showToast(props.t("Error"), "error")
            }
          })
          .catch(err => {
            console.log(err)
            showToast(props.t("Error"), "error")
          })
          .finally(() => {
            props.setOngoingAction(false)
          })
      }
    },
  })

  const virtualColumns = [
    {
      dataIndex: "vmr_number",
      title: props.t("Number"),
      width: "100px",
      ellipsis: true,
    },
    {
      dataIndex: "vmr_name",
      title: props.t("Name"),
      width: "140px",
      ellipsis: true,
    },
    {
      dataIndex: "require_host",
      title: props.t("Requires host"),
      align: "center",
      render: cell => {
        return cell == "1" ? (
          <i className="mdi mdi-check-bold text-primary" />
        ) : (
          <i className="mdi mdi-close-thick text-muted" />
        )
      },
      width: "120px",
      ellipsis: true,
    },
    {
      dataIndex: "actions",
      title: props.t("Actions"),
      align: "center",
      render: virtualEditFormatter,
      //fixed: "right",
      width: "100px",
      ellipsis: true,
    },
  ]

  function virtualEditFormatter(cell, row) {
    return (
      <>
        <Tooltip
          title={props.t("Edit")}
          placement="bottom"
          trigger={["hover", "focus"]}
        >
          <Button
            color="primary"
            outline
            size="sm"
            onClick={() => {
              let entry = new Object()
              entry.id = row.vmr_id
              entry.type = row.vmr_type
              entry.name = row.vmr_name
              entry.requiresHost = row.require_host == "1"
              entry.opensAt = "10:00:00"
              entry.closesAt = "14:00:00"
              entry.imageBase64 = row.image_base64

              props.setSelectedRoom(entry)
              props.setVirtualRoomsModal(true)
            }}
          >
            <i className="mdi mdi-pencil" />
          </Button>
        </Tooltip>
        <Tooltip
          title={props.t("Delete")}
          placement="bottom"
          trigger={["hover", "focus"]}
        >
          <Button
            className="ms-2"
            color="danger"
            outline
            size="sm"
            onClick={() => {
              props.setSelectedRoomId(row.vmr_id)
              props.setDeleteRoomModal(true)
            }}
          >
            <i className="mdi mdi-delete" />
          </Button>
        </Tooltip>
      </>
    )
  }

  return (
    <>
      {/* ACCORDION */}
      {props.loadingVirtualRooms ? (
        <>
          <div className="text-center">
            <Lottie
              options={getLottieLoadingOptions()}
              height={80}
              width={80}
            />
          </div>
        </>
      ) : (
        <>
          <Row>
            <Col></Col>
            <Col className="col-auto">
              <Button
                color="success"
                size="sm"
                className="d-flex"
                style={{ alignItems: "center" }}
                onClick={() => {
                  props.setNewRoom(1)
                }}
              >
                <i className="me-2 bx bx-calendar-plus text-truncate align-middle"></i>
                <span className="">{props.t("New room")}</span>
              </Button>
            </Col>
          </Row>
          <Row className="mt-2 mb-3">
            <Col>
              <AntdTable
                className="clear-header"
                size="small"
                rowKey={"vmr_id"}
                columns={virtualColumns}
                dataSource={props.virtualRooms}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: 50,
                  hideOnSinglePage: true,
                }}
                tableLayout="auto"
                scroll={{
                  x: "max-content",
                  y: "35vh",
                }}
              />
            </Col>
          </Row>
        </>
      )}

      {/* MODALS */}
      {/** Virtual room modal */}
      <Modal
        isOpen={props.virtualRoomsModal}
        toggle={() => {
          props.setVirtualRoomsModal(!props.virtualRoomsModal)
        }}
        centered
      >
        <div className="modal-header">
          {props.selectedRoom?.id != undefined ? (
            <h5 className="modal-title mt-0" id="myModalLabel">
              {props.t("Edit room")}
            </h5>
          ) : (
            <h5 className="modal-title mt-0" id="myModalLabel">
              {props.t("New room")}
            </h5>
          )}
          <button
            type="button"
            onClick={() => {
              props.setVirtualRoomsModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <>
          <div className="modal-body">
            <Form
              onSubmit={e => {
                e.preventDefault()
                return false
              }}
            >
              <FormGroup>
                <Label>{props.t("Name")}</Label>
                <AntdForm.Item
                  validateStatus={virtualValidation.errors.name ? "error" : ""}
                  hasFeedback
                  help={virtualValidation.errors.name}
                >
                  <AntdInput
                    allowClear
                    showCount
                    maxLength={30}
                    placeholder={props.t("Name")}
                    value={virtualValidation.values.name}
                    onChange={(v) => { virtualValidation.setFieldValue("name", v.target.value) }}
                  />
                </AntdForm.Item>
              </FormGroup>
              {props.selectedRoom?.type == 1 && (
                <>
                  <FormGroup>
                    <Switch
                      className="me-2"
                      name="requiresHost"
                      id="host-switch"
                      checked={virtualValidation.values.requiresHost}
                      onChange={v => {
                        virtualValidation.setFieldValue("requiresHost", v)
                      }}
                    ></Switch>
                    <Label>{props.t("Requires host")}</Label>
                    {/** 
                        <UncontrolledTooltip
                          placement="right"
                          target="host-switch"
                        >
                          {props.t("Info")}
                        </UncontrolledTooltip>*/}
                  </FormGroup>
                </>
              )}
              {props.selectedRoom?.type == 2 && (
                <>
                  <FormGroup>
                    <Label>{props.t("Opens at")}</Label>
                    <AntdForm.Item
                      validateStatus={virtualValidation.errors.opensAt ? "error" : ""}
                      hasFeedback
                      help={virtualValidation.errors.opensAt}
                    >
                      <AntdInput
                        allowClear
                        placeholder={"e.g. 10:00:00"}
                        value={virtualValidation.values.opensAt}
                        onChange={(v) => { virtualValidation.setFieldValue("opensAt", v.target.value) }}
                      />
                    </AntdForm.Item>
                  </FormGroup>
                  <FormGroup>
                    <Label>{props.t("Closes at")}</Label>
                    <AntdForm.Item
                      validateStatus={virtualValidation.errors.closesAt ? "error" : ""}
                      hasFeedback
                      help={virtualValidation.errors.closesAt}
                    >
                      <AntdInput
                        allowClear
                        placeholder={"e.g. 14:30:00"}
                        value={virtualValidation.values.closesAt}
                        onChange={(v) => { virtualValidation.setFieldValue("closesAt", v.target.value) }}
                      />
                    </AntdForm.Item>
                  </FormGroup>
                  <EventImagePicker
                    selected={virtualValidation.values.imageBase64}
                    selectionChanged={img => {
                      virtualValidation.setFieldValue("imageBase64", img)
                    }}
                  />
                </>
              )}

              <ModalFooter className="p-2 mt-2">
                <Col lg={12} className="mt-2 mb-0  text-truncate">
                  <Row>
                    <Col xs={12}>
                      <span className="float-end">
                        <button
                          type="button"
                          onClick={() => {
                            props.setVirtualRoomsModal(false)
                          }}
                          className="btn btn-outline-primary "
                          data-dismiss="modal"
                        >
                          {props.t("Cancel")}
                        </button>

                        <button
                          disabled={props.ongoingAction}
                          className="btn btn-primary ms-2"
                          onClick={() => {
                            virtualValidation.submitForm()
                          }}
                        >
                          {props.t("Save")}
                        </button>
                      </span>
                    </Col>
                  </Row>
                </Col>
              </ModalFooter>
            </Form>
          </div>
        </>
      </Modal>

      {/** Delete room modal */}
      <Modal
        isOpen={props.deleteRoomModal}
        toggle={() => {
          props.setDeleteRoomModal(!props.deleteRoomModal)
        }}
        centered
      >
        <div className="modal-header">
          <h5 className="modal-title mt-0" id="myModalLabel">
            {props.t("Delete room")}
          </h5>
          <button
            type="button"
            onClick={() => {
              props.setDeleteRoomModal(false)
            }}
            className="close"
            data-dismiss="modal"
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <ModalBody>
          {props.t("Are you sure you want to delete the selected room?")}
        </ModalBody>
        <ModalFooter className="p-2 mt-2">
          <Col lg={12} className="mt-2 mb-0  text-truncate">
            <Row>
              <Col xs={12}>
                <span className="float-end">
                  <button
                    type="button"
                    onClick={() => {
                      props.setDeleteRoomModal(false)
                    }}
                    className="btn btn-outline-primary "
                    data-dismiss="modal"
                  >
                    {props.t("Cancel")}
                  </button>

                  <button
                    disabled={props.ongoingAction}
                    className="btn btn-danger ms-2"
                    onClick={() => {
                      deleteRoom()
                    }}
                  >
                    {props.t("Delete")}
                  </button>
                </span>
              </Col>
            </Row>
          </Col>
        </ModalFooter>
      </Modal>
    </>
  )
}

VirtualRoomsContent.propTypes = {
  t: PropTypes.any,
  isProduction: PropTypes.any,
  ongoingAction: PropTypes.any,
  setOngoingAction: PropTypes.any,
  loadingVirtualRooms: PropTypes.any,
  virtualRoomsModal: PropTypes.any,
  selectedRoom: PropTypes.any,
  setSelectedRoom: PropTypes.func,
  virtualRooms: PropTypes.any,
  deleteRoomModal: PropTypes.any,
  getVirtualRooms: PropTypes.func,
  selectedRoomId: PropTypes.any,
  setSelectedRoomId: PropTypes.func,
  setNewRoom: PropTypes.func,
  setVirtualRoomsModal: PropTypes.func,
  setDeleteRoomModal: PropTypes.func,
  selectedTreeOption: PropTypes.any,
  getConversations: PropTypes.func,
}

export default withTranslation()(VirtualRoomsContent)
